import {
  RtCountableNumber,
  RtFraction,
  RtPeriodGranularity,
  RtPositiveInteger,
  RtWeekCount,
  RtWithProjectionPeriods,
  RtWithFundingType,
  RtWithName,
  RtWithRelativesSet,
} from "@aprao/common/src/runtypes/common.js";
import { z } from "zod";

import {
  TimelineOverrideSchema,
  WithTimelineOverrideSchema,
} from "./TimelineStageSchema.js";

export const RtRevenueItemBase = z.object({
  quantity: RtCountableNumber,
  description: z.string(),
});

export const RtRevenueGroupBase = RtWithFundingType.extend({
  name: z.string(),
});

/**
 * The base type for group of units to sale (residential or operated categories).
 */
export const RtSalesGroupBase = RtRevenueGroupBase.extend({
  linkedBuildPhaseRefId: z.string().nullable(),
});

export const RtResidentialPriceKeys = z.enum([
  "absolute",
  "ratioArea",
  "ratioItem",
]);
export const RtResidentialAreaKeys = z.enum(["absolute", "ratioItem"]);
export const RtResidentialSalesItem = RtRevenueItemBase.extend({
  area: RtWithRelativesSet(RtResidentialAreaKeys),
  salePrice: RtWithRelativesSet(RtResidentialPriceKeys),
});

export const RtResidentialDepositKeys = z.enum(["absolute", "ratioValueMean"]);
const RtResidentialFeeKeys = z.enum(["absolute", "ratioValue"]);
export const RtResidentialCostKeys = z.enum(["absolute", "ratioValue"]);

export const RtResidentialSalesGroup = RtSalesGroupBase.extend({
  category: z.literal("residential"),
  items: z.array(RtResidentialSalesItem),
  deposit: RtWithRelativesSet(RtResidentialDepositKeys),
  agentFee: RtWithRelativesSet(RtResidentialFeeKeys),
  legalFee: RtWithRelativesSet(RtResidentialFeeKeys),
  costs: z.array(RtWithRelativesSet(RtResidentialCostKeys).merge(RtWithName)),
  depositProjectionPeriods: z.array(RtPositiveInteger.nullable()),
  balanceProjectionPeriods: z.array(RtPositiveInteger.nullable()),
  depositTimelineOverride: TimelineOverrideSchema,
  balanceTimelineOverride: TimelineOverrideSchema,
});

export const RtOperatedAssetRentKeys = z.enum(["absolute", "ratioArea"]);
export const RtOperatedAssetItem = RtRevenueItemBase.extend({
  area: RtFraction.nullable(),
  periodRent: RtWithRelativesSet(RtOperatedAssetRentKeys),

  /**
   * The count of period where the unit is occupied. `null` means the whole year.
   *
   * It is typically in weeks, but can be in months following the parent group's
   * period granularity. Note that an annual occupancy in years doesn't make
   * sense since the value is calculated on a yield per year basis. We allow it,
   * but it would not impact the final value.
   */
  weekLetCount: RtWeekCount.nullable(),
});
export const RtOperatingCostKeys = z.enum([
  "absolute",
  "ratioAnnualRentalIncome",
  "ratioArea",
]);
const RtOperatingCost =
  RtWithRelativesSet(RtOperatingCostKeys).merge(RtWithName);
export const RtOperatedAssetCostKeys = z.enum([
  "absolute",
  "ratioAnnualRentalIncome",
  "ratioValue",
]);

export const RtOperatedAssetGroup = RtSalesGroupBase.merge(
  WithTimelineOverrideSchema,
).extend({
  category: z.literal("operated"),
  periodGranularity: RtPeriodGranularity,
  items: z.array(RtOperatedAssetItem),
  operatingCosts: z.array(RtOperatingCost),
  rentFreeMonthsCount: z.number().nullable(),
  yield: RtFraction.nullable(),
  costs: z.array(RtWithRelativesSet(RtOperatedAssetCostKeys).merge(RtWithName)),
  projectionPeriods: z.array(RtPositiveInteger.nullable()),
});

export const RtOtherIncomeItem = z.strictObject({
  description: z.string(),
  quantity: RtCountableNumber,
  value: RtFraction.nullable(),
});

export const RtOtherIncomeGroup = RtRevenueGroupBase.merge(
  RtWithProjectionPeriods,
)
  .merge(WithTimelineOverrideSchema)
  .extend({
    category: z.literal("other"),
    items: z.array(RtOtherIncomeItem),
    shouldIncludeInGdv: z.boolean(),
  });
